import { useState, useMemo } from "react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSave,
  faUserCheck,
  faSpinner,
  faSearch,
  faUserTie,
  faPhone,
  faEnvelope,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";
import Modal from "../../../components/ui/modal";
import {
  useAvailableOwners,
  useShopOwners,
} from "../../../hooks/useShopOwners";
import { toastSuccess, toastError } from "../../../utils/toast";

const AssignOwnerModal = ({ isOpen, onClose, onAssigned, shopId }) => {
  const [search, setSearch] = useState("");
  const [selectedUsernames, setSelectedUsernames] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { availableOwners, loading } = useAvailableOwners(
    shopId,
    search,
    isOpen,
  );
  const { createOwner } = useShopOwners(shopId);

  const reset = () => {
    setSearch("");
    setSelectedUsernames([]);
  };

  const handleClose = () => {
    if (!isSubmitting) {
      reset();
      onClose();
    }
  };

  const toggleOne = (username) => {
    setSelectedUsernames((prev) =>
      prev.includes(username)
        ? prev.filter((u) => u !== username)
        : [...prev, username],
    );
  };

  const allVisibleSelected = useMemo(() => {
    if (!availableOwners.length) return false;
    return availableOwners.every((o) => selectedUsernames.includes(o.username));
  }, [availableOwners, selectedUsernames]);

  const toggleAll = () => {
    if (allVisibleSelected) {
      const visible = availableOwners.map((o) => o.username);
      setSelectedUsernames((prev) => prev.filter((u) => !visible.includes(u)));
    } else {
      const visible = availableOwners.map((o) => o.username);
      setSelectedUsernames((prev) =>
        Array.from(new Set([...prev, ...visible])),
      );
    }
  };

  const handleSubmit = async () => {
    if (selectedUsernames.length === 0) {
      toastError("Please select at least one owner to assign");
      return;
    }

    try {
      setIsSubmitting(true);

      const results = await Promise.allSettled(
        selectedUsernames.map((username) =>
          createOwner({ shopId, username, status: "active" }),
        ),
      );

      const succeeded = results.filter((r) => r.status === "fulfilled").length;
      const failed = results.length - succeeded;

      if (succeeded > 0 && failed === 0) {
        toastSuccess(
          succeeded === 1
            ? "Owner assigned successfully!"
            : `${succeeded} owners assigned successfully!`,
        );
      } else if (succeeded > 0 && failed > 0) {
        toastSuccess(`${succeeded} owner(s) assigned`);
        toastError(`${failed} owner(s) failed to assign`);
      } else {
        toastError("Failed to assign owner(s). Please try again.");
      }

      if (succeeded > 0) {
        reset();
        onClose();
        if (onAssigned) await onAssigned();
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to assign owners";
      toastError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedCount = selectedUsernames.length;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Assign Existing Owners"
      maxWidth="sm:max-w-lg"
      footer={
        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
            className="w-full sm:w-auto px-6 border-2 border-orange-700 text-red-600 hover:text-white hover:bg-orange-600 hover:border-none"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || selectedCount === 0}
            className="gap-2 w-full sm:w-auto bg-blue-800 hover:bg-blue-900 text-white px-6 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <FontAwesomeIcon
                  icon={faSpinner}
                  className="h-4 w-4 animate-spin"
                />
                Assigning...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} />
                {selectedCount > 0
                  ? `Assign ${selectedCount} Owner${selectedCount > 1 ? "s" : ""}`
                  : "Assign Owner"}
              </>
            )}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-blue-50 to-white rounded-lg border border-blue-100">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
            <FontAwesomeIcon
              icon={faUserCheck}
              className="text-blue-800 h-4 w-4"
            />
          </div>
          <div>
            <p className="text-sm text-gray-700 font-semibold">
              Select one or more owners
            </p>
            <p className="text-sm text-gray-600">
              Only owners not yet linked to this shop appear here.
            </p>
          </div>
        </div>

        <div className="relative">
          <FontAwesomeIcon
            icon={faSearch}
            className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-500"
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, username, email or phone..."
            disabled={isSubmitting}
            className="pl-9 pr-3 h-10"
          />
        </div>

        {availableOwners.length > 0 && !loading && (
          <div className="flex items-center justify-between -mt-1">
            <button
              type="button"
              onClick={toggleAll}
              disabled={isSubmitting}
              className="text-xs font-medium text-blue-700 hover:text-blue-800 hover:underline disabled:opacity-50"
            >
              {allVisibleSelected ? "Unselect all" : "Select all"}
            </button>
            {selectedCount > 0 && (
              <span className="text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-2.5 py-0.5">
                {selectedCount} selected
              </span>
            )}
          </div>
        )}

        <div className="border border-gray-200 rounded-lg max-h-72 overflow-y-auto bg-white">
          {loading ? (
            <div className="flex items-center justify-center py-10 gap-2 text-gray-500 text-sm">
              <FontAwesomeIcon
                icon={faSpinner}
                className="animate-spin h-4 w-4"
              />
              Loading owners...
            </div>
          ) : availableOwners.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 gap-2 text-gray-500 text-sm">
              <FontAwesomeIcon
                icon={faUserTie}
                className="h-7 w-7 text-gray-300"
              />
              {search
                ? "No matching owners found"
                : "No available owners to assign"}
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {availableOwners.map((owner) => {
                const isSelected = selectedUsernames.includes(owner.username);
                return (
                  <li key={owner.username}>
                    <button
                      type="button"
                      onClick={() => toggleOne(owner.username)}
                      disabled={isSubmitting}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${
                        isSelected
                          ? "bg-green-50 border-l-4 border-green-600"
                          : "hover:bg-gray-50 border-l-4 border-transparent"
                      }`}
                    >
                      <span
                        className={`shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                          isSelected
                            ? "bg-green-600 border-green-600 text-white"
                            : "border-gray-300 bg-white"
                        }`}
                      >
                        {isSelected && (
                          <FontAwesomeIcon
                            icon={faCheck}
                            className="h-2.5 w-2.5"
                          />
                        )}
                      </span>

                      <div className="w-9 h-9 shrink-0 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-semibold">
                        {(owner.full_name || owner.username || "?")
                          .split(" ")
                          .map((p) => p[0])
                          .join("")
                          .slice(0, 2)
                          .toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {owner.full_name}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          @{owner.username}
                        </p>
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                          {owner.email && (
                            <span className="text-[11px] text-gray-500 flex items-center gap-1">
                              <FontAwesomeIcon
                                icon={faEnvelope}
                                className="h-2.5 w-2.5"
                              />
                              {owner.email}
                            </span>
                          )}
                          {owner.phone && (
                            <span className="text-[11px] text-gray-500 flex items-center gap-1">
                              <FontAwesomeIcon
                                icon={faPhone}
                                className="h-2.5 w-2.5"
                              />
                              {owner.phone}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default AssignOwnerModal;
